import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	map,
	switchMap,
	withLatestFrom,
	tap,
	combineLatest,
	of,
	filter,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { DashboardRequester } from '../../requesters/dashboard/dashboard.requester';
import {
	DashboardAction,
	DashboardActionPopupComponent,
	IDashboardActionModalData,
} from '../components/dashboard-action-popup/dashboard-action-popup.component';
import { DashboardAdapter } from '../dashboard.adapter';
import {
	bulkDeleteDashboards,
	deleteDashboard,
	deleteDashboardFail,
	deleteDashboardSuccess,
	fetchDashboardListFail,
	fetchDashboardListSuccess,
	initDashboardList,
	navigateToDashboard,
	navigateToDashboardCreate,
	navigateToDashboardsCarouselRoute,
	openDeleteConfirmationPopup,
	refreshDashboardList,
	toggleDashboardStatus,
	updateIncludePublic,
} from './dashboard.actions';
import { DashboardSelector } from './dashboard.selector';

@Injectable()
export class DashboardEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################

	public navigateToDepotCreate = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDashboardCreate),
				tap(() => this.stateService.go(states.DashboardCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToCarouselView = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDashboardsCarouselRoute),
				tap(() => this.stateService.go(states.DashboardCarousel))
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################

	public navigateToDashboard = createEffect(
		() =>
			this.action$.pipe(
				ofType(navigateToDashboard),
				tap((a) =>
					a.goToVisuTab
						? this.stateService.go(states.DashboardVisualization, {
								id: `${a.id}`,
								mode: 'visualisation',
						  })
						: this.stateService.go(states.DashboardEdit, {
								id: `${a.id}`,
						  })
				)
			),
		{
			dispatch: false,
		}
	);
	// Business Effects
	// ##########################################################

	public initDashboardList$ = createEffect(() =>
		this.action$.pipe(
			ofType(initDashboardList),
			withLatestFrom(this.dashboardSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.dashboardRequester.getDashboardList(includePublic)
			),
			map((response) =>
				fetchDashboardListSuccess({
					dashboardList: this.dashboardAdapter.generateDashboardList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDashboardListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshDashboardList$ = createEffect(() =>
		this.action$.pipe(
			ofType(refreshDashboardList),
			withLatestFrom(this.dashboardSelector.getIncludePublic$()),
			switchMap(([, includePublic]) =>
				this.dashboardRequester.getDashboardList(includePublic, false)
			),
			map((response) =>
				fetchDashboardListSuccess({
					dashboardList: this.dashboardAdapter.generateDashboardList(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchDashboardListFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateIncludePublic$ = createEffect(() =>
		this.action$.pipe(
			ofType(updateIncludePublic),
			map(() => refreshDashboardList())
		)
	);

	public deleteDashboards$ = createEffect(() =>
		this.action$.pipe(
			ofType(deleteDashboard),
			switchMap((action) =>
				this.dashboardRequester.deleteDashboard(action.dashboardId)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@dashboard.delete.success:`
				);
				this.store.dispatch(deleteDashboardSuccess());
				return refreshDashboardList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@dashboard.delete.error:`
				);
				this.store.dispatch(
					deleteDashboardFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public bulkDeleteDashboards$ = createEffect(() =>
		this.action$.pipe(
			ofType(bulkDeleteDashboards),
			switchMap((action) =>
				this.dashboardRequester.deleteMany(action.dashboardIds)
			),
			map(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@dashboard.delete.multiple.success:`
				);
				this.store.dispatch(deleteDashboardSuccess());
				return refreshDashboardList();
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@dashboard.delete.multiple.error:`
				);
				this.store.dispatch(
					deleteDashboardFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleActivationStatus$ = createEffect(() =>
		this.action$.pipe(
			ofType(toggleDashboardStatus),
			switchMap((action) =>
				combineLatest([
					of(action),
					this.dashboardRequester.getDashboard(action.dashboardId),
				])
			),
			switchMap(([action, dashboard]) =>
				this.dashboardRequester.changeActivationStatusDashboard({
					...dashboard,
					metadata: {
						...dashboard.metadata,
						actif: action.status,
					},
				})
			),
			map(() => refreshDashboardList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(refreshDashboardList());
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openDeleteConfirmationPopup$ = createEffect(() =>
		this.action$.pipe(
			ofType(openDeleteConfirmationPopup),
			switchMap((action) => {
				const modalRef = this.modalService.openPopup<IDashboardActionModalData>(
					DashboardActionPopupComponent,
					{
						title: $localize`:i18n=@@dashboard.action.popup.delete.header:`,
						subTitle: '',
						dashboardId: action.dashboardId,
						element: action.dashboardLabel,
						action: DashboardAction.Delete,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (action.dashboardId.length === 1) {
					return deleteDashboard({
						dashboardId: action.dashboardId[0],
					});
				} else {
					return bulkDeleteDashboards({
						dashboardIds: action.dashboardId,
					});
				}
			})
		)
	);
	public constructor(
		private readonly action$: Actions,
		private readonly dashboardRequester: DashboardRequester,
		private readonly dashboardAdapter: DashboardAdapter,
		private readonly dashboardSelector: DashboardSelector,
		private readonly stateService: AjsStateService,
		private readonly store: Store,
		private readonly modalService: AppModalService,
		private readonly ngCommService: NgCommService
	) {}
}
